import { Extension } from "@tiptap/core";
import { Node as PMNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { hashText } from "../../services/proofread/hash";
import type { ProofSuggestion } from "../../services/proofread/types";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    proofread: {
      /** Replace the suggestions for one block (keyed by its text hash). */
      setProofSuggestions: (
        blockHash: string,
        suggestions: ProofSuggestion[],
      ) => ReturnType;
      /** Snapshot every block as "untouched"; only later edits get linted. */
      setProofBaseline: () => ReturnType;
      applyProofSuggestion: (id: string, replacement: string) => ReturnType;
      dismissProofSuggestion: (id: string) => ReturnType;
      clearProofSuggestions: () => ReturnType;
      setProofreadEnabled: (enabled: boolean) => ReturnType;
      setActiveProofSuggestion: (id: string | null) => ReturnType;
    };
  }
}

/**
 * One textblock flattened to plain text plus a map from each UTF-16 index to
 * its absolute document position (hard breaks count as "\n"). Same technique
 * as ReplaceText.findReplacementRanges, so server spans line up exactly.
 */
export interface ProofBlock {
  hash: string;
  text: string;
  posMap: number[];
  /** Absolute positions of the block's content, for range queries. */
  from: number;
  to: number;
}

function flattenBlock(node: PMNode, pos: number): { text: string; posMap: number[] } {
  let text = "";
  const posMap: number[] = [];
  node.forEach((child, offset) => {
    const base = pos + 1 + offset;
    if (child.isText && child.text) {
      for (let i = 0; i < child.text.length; i++) posMap.push(base + i);
      text += child.text;
    } else if (child.type.name === "hardBreak") {
      posMap.push(base);
      text += "\n";
    } else {
      // Any other inline leaf: pad with spaces so offsets stay aligned.
      for (let i = 0; i < child.nodeSize; i++) posMap.push(base + i);
      text += " ".repeat(child.nodeSize);
    }
  });
  return { text, posMap };
}

// Headings are titles, not prose; everything else that holds text is linted
// (paragraphs, list items, table cells).
export function collectProofBlocks(doc: PMNode): ProofBlock[] {
  const blocks: ProofBlock[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.name === "heading") return false;
    const { text, posMap } = flattenBlock(node, pos);
    if (text.trim()) {
      blocks.push({
        hash: hashText(text),
        text,
        posMap,
        from: pos + 1,
        to: pos + 1 + node.content.size,
      });
    }
    return false;
  });
  return blocks;
}

export interface ProofreadStorage {
  baseline: Set<string>;
  activeId: string | null;
  /** Doc range of the decoration the officer clicked (duplicates disambiguated). */
  activeRange: { from: number; to: number } | null;
  enabled: boolean;
  _version: number;
}

interface ProofSpec {
  proofId: string;
  suggestion: ProofSuggestion;
}

type ProofMeta =
  | { type: "set"; blockHash: string; suggestions: ProofSuggestion[] }
  | { type: "dismiss"; id: string }
  | { type: "clear" }
  | { type: "active" }
  | { type: "enabled" }
  | { type: "applied" };

export const proofreadPluginKey = new PluginKey<DecorationSet>("proofread");

/** The suggestion behind a decoration id, read from the live decoration set. */
export function findSuggestion(
  state: EditorState,
  id: string,
): ProofSuggestion | null {
  const decorations = proofreadPluginKey.getState(state);
  if (!decorations) return null;
  const match = decorations.find(undefined, undefined, (spec) => spec.proofId === id)[0];
  return match ? (match.spec as ProofSpec).suggestion : null;
}

// Prefer the decoration the officer clicked when identical paragraphs
// produced the same id twice.
function findDecoration(
  decorations: DecorationSet,
  id: string,
  preferFrom?: number,
): Decoration | null {
  const matches = decorations.find(undefined, undefined, (spec) => spec.proofId === id);
  if (!matches.length) return null;
  if (preferFrom !== undefined) {
    const exact = matches.find((d) => d.from === preferFrom);
    if (exact) return exact;
  }
  return matches[0];
}

function makeDecoration(
  from: number,
  to: number,
  suggestion: ProofSuggestion,
  active: boolean,
): Decoration {
  return Decoration.inline(
    from,
    to,
    {
      class: `proof-mark proof-mark-${suggestion.category}${active ? " proof-mark-active" : ""}`,
      "data-proof-id": suggestion.id,
    },
    { proofId: suggestion.id, suggestion } as ProofSpec,
  );
}

// Decorations for every block whose text hashes to `blockHash`.
function decorationsForBlock(
  doc: PMNode,
  blockHash: string,
  suggestions: ProofSuggestion[],
  activeId: string | null,
): { blocks: ProofBlock[]; decorations: Decoration[] } {
  const blocks = collectProofBlocks(doc).filter((b) => b.hash === blockHash);
  const decorations: Decoration[] = [];
  for (const block of blocks) {
    for (const s of suggestions) {
      if (s.end > block.text.length) continue;
      if (block.text.slice(s.start, s.end) !== s.original) continue;
      decorations.push(
        makeDecoration(
          block.posMap[s.start],
          block.posMap[s.end - 1] + 1,
          s,
          s.id === activeId,
        ),
      );
    }
  }
  return { blocks, decorations };
}

// After an edit, keep every mark whose underlying text is still exactly what
// was flagged and drop the rest. Gmail behaves the same way: the word you
// are editing loses its underline, the others stay put.
function mapThroughEdit(decorations: DecorationSet, tr: Transaction): DecorationSet {
  const mapped = decorations.map(tr.mapping, tr.doc);
  const stale = mapped.find().filter((d) => {
    const { suggestion } = d.spec as ProofSpec;
    return tr.doc.textBetween(d.from, d.to, undefined, "\n") !== suggestion.original;
  });
  return stale.length ? mapped.remove(stale) : mapped;
}

function withActiveClass(
  decorations: DecorationSet,
  doc: PMNode,
  activeId: string | null,
): DecorationSet {
  const all = decorations.find();
  if (!all.length) return decorations;
  return DecorationSet.create(
    doc,
    all.map((d) => {
      const { suggestion } = d.spec as ProofSpec;
      return makeDecoration(d.from, d.to, suggestion, suggestion.id === activeId);
    }),
  );
}

export const Proofread = Extension.create<{}, ProofreadStorage>({
  name: "proofread",

  addStorage() {
    return {
      baseline: new Set(),
      activeId: null,
      activeRange: null,
      enabled: true,
      _version: 0,
    };
  },

  addCommands() {
    const touch = (storage: ProofreadStorage) => {
      storage._version += 1;
    };
    const meta = (tr: Transaction, m: ProofMeta) => tr.setMeta(proofreadPluginKey, m);

    return {
      setProofSuggestions:
        (blockHash, suggestions) =>
        ({ editor, tr, dispatch, state }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          if (storage.activeId) {
            const active = findSuggestion(state, storage.activeId);
            if (active?.blockHash === blockHash) {
              storage.activeId = null;
              storage.activeRange = null;
            }
          }
          touch(storage);
          if (dispatch) meta(tr, { type: "set", blockHash, suggestions });
          return true;
        },

      setProofBaseline:
        () =>
        ({ editor, tr, dispatch, state }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          storage.baseline = new Set(collectProofBlocks(state.doc).map((b) => b.hash));
          storage.activeId = null;
          storage.activeRange = null;
          touch(storage);
          if (dispatch) meta(tr, { type: "clear" });
          return true;
        },

      applyProofSuggestion:
        (id, replacement) =>
        ({ editor, tr, dispatch, state }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          const decorations = proofreadPluginKey.getState(state);
          if (!decorations) return false;
          const target = findDecoration(
            decorations,
            id,
            storage.activeId === id ? storage.activeRange?.from : undefined,
          );
          if (!target) return false;
          const { suggestion } = target.spec as ProofSpec;
          if (
            state.doc.textBetween(target.from, target.to, undefined, "\n") !==
            suggestion.original
          ) {
            return false;
          }
          if (!dispatch) return true;
          // insertText keeps the surrounding marks; the replaced span no
          // longer matches its suggestion, so mapThroughEdit drops that one
          // mark and keeps the paragraph's others.
          tr.insertText(replacement, target.from, target.to);
          storage.activeId = null;
          storage.activeRange = null;
          touch(storage);
          meta(tr, { type: "applied" });
          return true;
        },

      dismissProofSuggestion:
        (id) =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          if (storage.activeId === id) {
            storage.activeId = null;
            storage.activeRange = null;
          }
          touch(storage);
          if (dispatch) meta(tr, { type: "dismiss", id });
          return true;
        },

      clearProofSuggestions:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          storage.activeId = null;
          storage.activeRange = null;
          touch(storage);
          if (dispatch) meta(tr, { type: "clear" });
          return true;
        },

      setProofreadEnabled:
        (enabled) =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          storage.enabled = enabled;
          if (!enabled) {
            storage.activeId = null;
            storage.activeRange = null;
          }
          touch(storage);
          if (dispatch) meta(tr, { type: "enabled" });
          return true;
        },

      setActiveProofSuggestion:
        (id) =>
        ({ editor, tr, dispatch, state }) => {
          const storage = editor.storage.proofread as ProofreadStorage;
          if (id) {
            const decorations = proofreadPluginKey.getState(state);
            const target = decorations ? findDecoration(decorations, id) : null;
            if (!target) return false;
            storage.activeId = id;
            storage.activeRange = { from: target.from, to: target.to };
          } else {
            storage.activeId = null;
            storage.activeRange = null;
          }
          touch(storage);
          if (dispatch) meta(tr, { type: "active" });
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin<DecorationSet>({
        key: proofreadPluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old) {
            const storage = editor.storage.proofread as ProofreadStorage;
            const m = tr.getMeta(proofreadPluginKey) as ProofMeta | undefined;

            // Ordinary typing: carry every surviving mark along.
            let next = tr.docChanged ? mapThroughEdit(old, tr) : old;
            if (!m) return next;

            if (!storage.enabled) return DecorationSet.empty;

            switch (m.type) {
              case "set": {
                const { blocks, decorations } = decorationsForBlock(
                  tr.doc,
                  m.blockHash,
                  m.suggestions,
                  storage.activeId,
                );
                // Replace whatever the block had before (marks mapped through
                // earlier edits) with the fresh check.
                for (const block of blocks) {
                  const existing = next.find(block.from, block.to);
                  if (existing.length) next = next.remove(existing);
                }
                if (decorations.length) next = next.add(tr.doc, decorations);
                return next;
              }
              case "dismiss": {
                const matches = next.find(undefined, undefined, (spec) => spec.proofId === m.id);
                return matches.length ? next.remove(matches) : next;
              }
              case "clear":
                return DecorationSet.empty;
              case "enabled":
                return next;
              case "active":
                return withActiveClass(next, tr.doc, storage.activeId);
              case "applied":
                return withActiveClass(next, tr.doc, null);
              default:
                return next;
            }
          },
        },
        props: {
          decorations(state) {
            return editor.isEditable ? this.getState(state) : DecorationSet.empty;
          },
          // Clicking an underline opens the popover for that exact
          // occurrence. The caret still lands where the officer clicked.
          handleClick(view, _pos, event) {
            const target = (event.target as HTMLElement | null)?.closest?.(
              "[data-proof-id]",
            ) as HTMLElement | null;
            if (!target) return false;
            const id = target.getAttribute("data-proof-id");
            if (!id) return false;
            const decorations = proofreadPluginKey.getState(view.state);
            if (!decorations) return false;
            // Pick the occurrence under the click when identical paragraphs
            // share an id.
            const matches = decorations.find(undefined, undefined, (spec) => spec.proofId === id);
            if (!matches.length) return false;
            const match =
              matches.find((d) => _pos >= d.from && _pos <= d.to) || matches[0];
            const storage = editor.storage.proofread as ProofreadStorage;
            storage.activeId = id;
            storage.activeRange = { from: match.from, to: match.to };
            storage._version += 1;
            view.dispatch(view.state.tr.setMeta(proofreadPluginKey, { type: "active" }));
            return true;
          },
        },
      }),
    ];
  },
});

export default Proofread;
